<template>
    <div>
        <canvas
            ref="canvas"
            style="display: block; width: 100%; height: auto;"
            @mouseover="onMouseOver"
            @mouseout="onMouseOut"
        ></canvas>
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularDirective } from '@/vue/decorators/angular-interop';
import { Alignment, Fit, Layout, Rive } from '@rive-app/canvas';

@Component({
    name: 'RiveAnimation',
    props: {
        /**
         * Path to the animation source file.
         */
        src: {
            type: String,
            required: true,
        },
        /**
         * Fit of the animation within the given canvas.
         *
         * Should be one of the available Fit values from @rive-app/canvas
         */
        fit: {
            type: String,
            required: false,
            default: Fit.Contain,
            validator(value) {
                return Object.values(Fit).includes(value);
            },
        },
        /**
         * Alignment of the animation within the given canvas.
         *
         * Should be one of the available Alignment values from @rive-app/canvas
         */
        alignment: {
            type: String,
            required: false,
            default: Alignment.Center,
            validator(value) {
                return Object.values(Alignment).includes(value);
            },
        },
        /**
         * Should the animation play automatically?
         */
        autoplay: {
            type: Boolean,
            required: false,
            default: true,
        },
        /**
         * Specific width for the animation canvas.
         */
        width: {
            type: Number,
            required: false,
            default: null,
        },
        /**
         * Specific height for the animation canvas.
         */
        height: {
            type: Number,
            required: false,
            default: null,
        },
        /**
         * Disable Rive listeners.
         *
         * Can be useful to prevent conflicts between page interactions and animation listeners.
         */
        disableRiveListeners: {
            type: Boolean,
            required: false,
            default: false,
        },
        /**
         * Disable Rive listeners.
         *
         * Can be useful to prevent conflicts between page interactions and animation listeners.
         */
        stateMachines: {
            type: Array,
            required: false,
            default: () => ['State Machine 1'],
        },
    },
    data() {
        return {
            rive: null,
        };
    },
    emits: [
        'load',
        'play',
        'pause',
        'stop',
        'state-change',
        'advance',
        'loop',
        'load-error',
        'mouseover',
        'mouseout',
    ],
    mounted() {
        // eslint-disable-next-line no-unused-vars
        this.rive = new Rive({
            canvas: this.$refs.canvas,
            src: this.$props.src,
            layout: new Layout({
                fit: this.$props.fit,
                alignment: this.$props.alignment,
            }),
            stateMachines: this.stateMachines || ['State Machine 1'],
            shouldDisableRiveListeners: this.disableRiveListeners,
            autoplay: this.$props.autoplay,
            onLoad: e => {
                this.rive.resizeDrawingSurfaceToCanvas();

                /**
                 * Emitted when the animation is loaded.
                 *
                 * @event load
                 * @type {Event}
                 */
                this.$emit('load', this.createEvent(e));
            },
            onPlay: e => {
                /**
                 * Emitted when one or more animations are played.
                 *
                 * Provides a list of animations.
                 *
                 * @event play
                 * @type {Event}
                 */
                this.$emit('play', this.createEvent(e));
            },
            onPause: e => {
                /**
                 * Emitted when one or more animations are paused.
                 *
                 * Provides a list of animations.
                 *
                 * @event pause
                 * @type {Event}
                 */
                this.$emit('pause', this.createEvent(e));
            },
            onStop: e => {
                /**
                 * Emitted when one or more animations are stopped.
                 *
                 * Provides a list of animations.
                 *
                 * @event stop
                 * @type {Event}
                 */
                this.$emit('stop', this.createEvent(e));
            },
            onStateChange: e => {
                /**
                 * Emitted when the animation state is changed.
                 *
                 * @event state-changed
                 * @type {Event}
                 */
                this.$emit('state-change', this.createEvent(e));
            },
            onAdvance: e => {
                this.$emit('advance', this.createEvent(e));
            },
            onLoop: e => {
                /**
                 * Emitted when an animation loops.
                 *
                 * Provides the animation name.
                 *
                 * @event loop
                 * @type {Event}
                 */
                this.$emit('loop', this.createEvent(e));
            },
            onLoadError: e => {
                /**
                 * Emitted when an error occurs in one or more animations.
                 *
                 * Provides a list of animations.
                 *
                 * @event stop
                 * @type {Event}
                 */
                this.$emit('load-error', this.createEvent(e));
            },
        });
    },
    methods: {
        /**
         * Updates a Rive event to include the Rive instance.
         *
         * @param e
         * @returns {*}
         */
        createEvent(e) {
            let event = e;
            event['rive'] = this.rive;
            return event;
        },
        onMouseOver(e) {
            this.$emit('mouseover', e);
        },
        onMouseOut(e) {
            this.$emit('mouseout', e);
        },
    },
})
@AngularDirective('rive-animation')
export default class RiveAnimation extends Vue {}
</script>
